import './TagsTreePanel.scss';
import React, {useState, useEffect, useMemo} from 'react';
import ArrTreeView from '../../../../common/arr-tree-view/ArrTreeView';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  ExtendedTreeItemProps,
  TooltipData,
} from '../../../../../shared/interfaces';
import {Box, Drawer, Typography, useMediaQuery, useTheme} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../../src/assets/images/info-icon.svg';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import {panelWidths} from '../../../../../shared/constants';

type TagsTreePanelProps =
  | {
      tags: TreeViewBaseItem<ExtendedTreeItemProps>[];
      type?: 'normal';
      isOpen?: never;
      title?: never;
      ariaLabel?: string;
      tooltip?: never;
      isPreExpanded?: boolean;
      selectedTags?: TreeViewBaseItem<ExtendedTreeItemProps>[];
      noResultText?: string;
      setSelectedTags?: (
        values: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ) => void;
      onClose?: () => never;
      selectedButtonName?: string;
      isSelectGroupItem?: boolean;
      moreItemSection?: never;
    }
  | {
      tags: TreeViewBaseItem<ExtendedTreeItemProps>[];
      type: 'flyout';
      isOpen: boolean;
      title?: string;
      onClose: () => void;
      ariaLabel?: string;
      tooltip?: TooltipData;
      isPreExpanded?: boolean;
      selectedTags?: TreeViewBaseItem<ExtendedTreeItemProps>[];
      noResultText?: string;
      setSelectedTags?: (
        values: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ) => void;
      selectedButtonName?: string;
      isSelectGroupItem?: boolean;
      moreItemSection?: React.ReactNode;
    };

const TagsTreePanel: React.FC<TagsTreePanelProps> = ({
  tags,
  type = 'normal',
  isOpen = false,
  title = t(
    'productionsAndExports.createNewProduction.common.tagsTreePanel.header.title'
  ),
  ariaLabel = t(
    'productionsAndExports.createNewProduction.common.tagsTreePanel.header.title'
  ),
  tooltip = {
    title: t(
      'productionsAndExports.createNewProduction.common.tagsTreePanel.tooltips.selectTags.title'
    ),
    content: t(
      'productionsAndExports.createNewProduction.common.tagsTreePanel.tooltips.selectTags.content'
    ),
  },
  noResultText = t(
    'productionsAndExports.createNewProduction.common.tagsTreePanel.content.noResults'
  ),
  isPreExpanded = true,
  selectedTags,
  setSelectedTags,
  onClose,
  selectedButtonName,
  isSelectGroupItem = false,
  moreItemSection,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const width = panelWidths.small;
  const transitionDuration = 300;
  const customTreeContentHeight = 48;

  const findSelectedItems = (
    tree: TreeViewBaseItem<ExtendedTreeItemProps>[],
    selectedValues: string[]
  ): ExtendedTreeItemProps[] => {
    const result: ExtendedTreeItemProps[] = [];

    const search = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      for (const node of nodes) {
        if (selectedValues.includes(node.id)) {
          if (isSelectGroupItem) {
            result.push(node);
          } else {
            if (!node.isGroup && node.parentGroupId != '#') result.push(node);
          }
        }
        if (node.children) {
          search(node.children);
        }
      }
    };

    search(tree);
    return result;
  };

  const extractAllIds = (
    tree: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): string[] => {
    const ids: string[] = [];

    const traverse = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
      for (const node of nodes) {
        if (node.isGroup) ids.push(node.id);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };

    traverse(tree);
    return ids;
  };

  // Find parent group IDs for selected tags by building a node map and traversing up
  const findParentGroupIds = (
    tree: TreeViewBaseItem<ExtendedTreeItemProps>[],
    selectedTagIds: string[]
  ): string[] => {
    // Build a map of all nodes by ID
    const nodeMap = new Map<string, TreeViewBaseItem<ExtendedTreeItemProps>>();

    const buildMap = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): void => {
      for (const node of nodes) {
        nodeMap.set(node.id, node);
        if (node.children && node.children.length > 0) {
          buildMap(node.children);
        }
      }
    };

    buildMap(tree);

    const parentGroupIds = new Set<string>();

    // For each selected tag, traverse up the parentGroupId chain
    for (const tagId of selectedTagIds) {
      let currentNode = nodeMap.get(tagId);
      while (currentNode) {
        const parentId = currentNode.parentGroupId;
        if (parentId && parentId !== '#' && parentId !== tagId) {
          parentGroupIds.add(parentId);
          currentNode = nodeMap.get(parentId);
        } else {
          break;
        }
      }
    }

    return Array.from(parentGroupIds);
  };

  // Memoize expandableIds to recompute when tags or selectedTags change
  const expandableIds = useMemo(() => {
    if (!isPreExpanded) {
      return [];
    }

    // If we have selected tags, expand their parent groups
    if (selectedTags && selectedTags.length > 0) {
      const selectedTagIds = selectedTags.map((tag) => tag.id);
      const parentGroupIds = findParentGroupIds(tags, selectedTagIds);
      // Also include all group IDs to ensure full expansion
      const allGroupIds = extractAllIds(tags);
      // Combine and deduplicate
      return Array.from(new Set([...parentGroupIds, ...allGroupIds]));
    }

    // Otherwise, expand all groups
    return extractAllIds(tags);
  }, [tags, selectedTags, isPreExpanded]);

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    extractAllIds(selectedTags || [])
  );

  // Sync internal state with selectedTags prop changes
  useEffect(() => {
    const ids = (selectedTags || []).map((tag) => tag.id);
    setSelectedTagIds(ids);
  }, [selectedTags]);

  const handleSelected = (values: string[] | null) => {
    setSelectedTagIds(values || []);
    if (type == 'normal') {
      setSelectedTags?.(findSelectedItems(tags, values || []));
    }
  };

  const handleSelectSubmit = () => {
    setSelectedTags?.(findSelectedItems(tags, selectedTagIds || []));
    if (type == 'flyout') {
      onClose?.();
    }
  };

  return (
    <>
      {type == 'flyout' && (
        <Drawer
          anchor={'right'}
          transitionDuration={transitionDuration}
          open={isOpen}
          className="arr-panel"
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onClose={(event, reason) => {
            onClose?.();
          }}
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              className: 'arr-drawer__backdrop',
            },
          }}
          slotProps={{
            paper: {
              className: `arr-drawer__panel`,
              sx: {
                width: isSmallScreen ? '100%' : `${width}rem`,
              },
              'aria-label': ariaLabel || title,
            },
          }}
        >
          <Box className="arr-drawer__content">
            <Box className="add-tags-container">
              <Box className="add-tags-header-box">
                <Box className="title-box">
                  <Typography
                    className=" typo-text headline3_size title"
                    data-testid={`tags-tree-panel-title`}
                  >
                    {title}&nbsp;
                  </Typography>
                  <ArrTooltipWrapper
                    trigger={
                      <InfoIcon fontSize="medium" className="info-icon" />
                    }
                    title={tooltip.title}
                  >
                    {typeof tooltip.content === 'string' ? (
                      <Trans
                        defaults={tooltip.content}
                        components={{
                          br: <br data-testid={`tags-tree-panel-br`} />,
                        }}
                      />
                    ) : (
                      tooltip.content || title
                    )}
                  </ArrTooltipWrapper>
                </Box>
                <Box className="action-buttons-box">
                  <ArrButton
                    variant="secondary"
                    size="medium"
                    onClick={onClose}
                    disabled={false}
                    dataTestId="tags-tree-panel-cancel-button"
                  >
                    {t('common.cancel')}
                  </ArrButton>
                  <ArrButton
                    variant="primary"
                    size="medium"
                    type="submit"
                    dataTestId="select-tags-button"
                    disabled={false}
                    onClick={handleSelectSubmit}
                  >
                    {selectedButtonName
                      ? selectedButtonName
                      : t('common.select')}
                  </ArrButton>
                </Box>
              </Box>
              <Box className="tags-tree-container">
                <Box className="tree-item-section">
                  <ArrTreeView
                    itemsList={tags}
                    allowGlobalSelect={false}
                    treeSelectedItems={selectedTagIds}
                    multipleSelection={true}
                    isShowCountLabel={false}
                    isShowCheckbox={true}
                    customContentHeight={customTreeContentHeight}
                    setSelectedItem={handleSelected}
                    handleNoResultMessage={noResultText}
                    includeDisabledInSelectionState={true}
                    {...(isPreExpanded && {defaultExpandedIds: expandableIds})}
                  />
                </Box>
                {moreItemSection && (
                  <Box className="more-item-section">{moreItemSection}</Box>
                )}
              </Box>
            </Box>
          </Box>
        </Drawer>
      )}
      {type == 'normal' && (
        <Box className="tags-tree-container">
          <ArrTreeView
            itemsList={tags}
            allowGlobalSelect={false}
            treeSelectedItems={selectedTagIds}
            multipleSelection={true}
            isShowCountLabel={false}
            isShowCheckbox={true}
            customContentHeight={customTreeContentHeight}
            setSelectedItem={handleSelected}
            handleNoResultMessage={noResultText}
            includeDisabledInSelectionState={true}
            {...(isPreExpanded && {defaultExpandedIds: expandableIds})}
          />
        </Box>
      )}
    </>
  );
};

export default TagsTreePanel;
